import * as React from 'react';
import { EventFragment } from '@graphql/types';
import { BackgroundType } from '@routes/event/create/DesignDemo/config/brannanThemeSupport';

export const BrannanBackgroundType = {
  CLASSIC: 'classic' as 'classic',
  MODERN: 'modern' as 'modern',
  MINIMAL: 'minimal' as 'minimal',
  THEME: 'theme' as 'theme'
};

export type BrannanBackgroundType = typeof BrannanBackgroundType[keyof typeof BrannanBackgroundType];

type BrannanSetting = {
  isEnabled: boolean;
};

export const BrannanSettingProperty = {
  BACKGROUND_TRANSLUCENCY: 'backgroundTranslucency' as 'backgroundTranslucency',
  COLORS: 'colors' as 'colors'
};

export type BrannanSettingProperty = typeof BrannanSettingProperty[keyof typeof BrannanSettingProperty];

export type NewThemeSettings = {
  backgroundType: BrannanBackgroundType;
  backgroundTranslucency: BrannanSetting;
  customBackgroundColor: BrannanSetting;
};

type Handlers = {
  updateBackgroundType: (backgroundType: BrannanBackgroundType) => void;
  updateSetting: (property: BrannanSettingProperty, preferences: BrannanSetting) => void;
  reset: () => void;
};

type BrannanContextValue = NewThemeSettings & Handlers;

export const INITIAL_THEME_SETTINGS = (event?: EventFragment): NewThemeSettings => {
  if (event) {
    return {
      backgroundTranslucency: { isEnabled: event.style.translucent },
      customBackgroundColor: { isEnabled: true },
      backgroundType: (event.style.backgroundType || BackgroundType.theme) as BackgroundType
    };
  }
  return {
    backgroundTranslucency: { isEnabled: false },
    customBackgroundColor: { isEnabled: true },
    backgroundType: 'theme'
  };
};
// textColor: '#333',
// backgroundColor: '#fff'

export const ThemeContext = React.createContext<NewThemeSettings>({
  ...INITIAL_THEME_SETTINGS()

  // updateBackgroundType: () => {
  //   throw new Error('ThemeContext should be used within a BrannanProvider');
  // },
  // updateSetting: () => {
  //   throw new Error('ThemeContext should be used within a BrannanProvider');
  // },
  // reset: () => {
  //   throw new Error('ThemeContext should be used within a BrannanProvider');
  // }
});

export const BrannanProvider: React.FC<{ value: NewThemeSettings }> = ({ children, value }) => {
  const [state, setState] = React.useState<NewThemeSettings>(INITIAL_THEME_SETTINGS());

  const updateBackgroundType = React.useCallback<Handlers['updateBackgroundType']>(
    backgroundType => {
      setState(prevState => ({ ...prevState, backgroundType }));
    },
    [state.backgroundType]
  );

  const updateSetting = React.useCallback<Handlers['updateSetting']>(
    (property, settings) => {
      setState(prevState => ({
        ...prevState,
        [property]: settings
      }));
    },
    [state]
  );

  const reset = React.useCallback(() => {
    setState(INITIAL_THEME_SETTINGS());
  }, []);

  const providerValue = React.useMemo<BrannanContextValue>(
    () => {
      return {
        ...state,
        updateBackgroundType,
        updateSetting,
        reset
      };
    },
    [state]
  );

  (window as any).test = providerValue;

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeSettingsContext = () => {
  return React.useContext(ThemeContext);
};
